import type { PanelFilters } from '@content-search/domain';
import { useStandardLabels } from '@content-search/hooks';
import { FEATURE_FLAGS } from '@core/domain';
import { ContentType } from '@core/graphql/types/schema';
import { useIsFeatureEnabled } from '@core/hooks';
import { MultiSelect } from '@soteria/design-system-ve';
import { useMemo, useCallback } from 'react';

import * as styles from './FilterPanel.css';

interface LabelsFilterProps {
  panelFilters: PanelFilters;
  updateFilters: (updates: Partial<PanelFilters>) => void;
  isLoading: boolean;
}
// Transform labels for MultiSelect component
const getLabelOptions = (standardLabels: Array<{ id: string; name: string; value: string }>) =>
  standardLabels.map(label => ({
    id: label.id,
    label: label.name,
    value: label.value,
  }));

export function LabelsFilter({ panelFilters, updateFilters, isLoading }: LabelsFilterProps) {
  const { standardLabels } = useStandardLabels();
  const isUsernameClassificationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  const labelOptions = useMemo(
    () => getLabelOptions(isUsernameClassificationEnabled ? standardLabels : standardLabels.filter(label => label.contentType !== ContentType.Username)),
    [standardLabels, isUsernameClassificationEnabled]
  );

  const handleLabelsChange = useCallback(
    (selectedLabels: string[]) => {
      updateFilters({ labels: selectedLabels });
    },
    [updateFilters]
  );

  return (
    <div className={styles.filterSection}>
      <div className={styles.filterLabel}>Labels</div>
      <MultiSelect
        searchable
        aria-label="Filter by labels"
        disabled={isLoading}
        options={labelOptions}
        placeholder="All"
        searchPlaceholder="Search labels..."
        selectedValues={panelFilters.labels || []}
        onSelectionChange={handleLabelsChange}
      />
    </div>
  );
}
